import React, { useState } from 'react';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FormHelperText from '@mui/material/FormHelperText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Input from '@mui/material/Input';
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import Chip from '@mui/material/Chip';
import CancelIcon from "@mui/icons-material/Cancel";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import { useTheme } from '@mui/material/styles';

const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: 48 * 4.5 + 8,
            width: 250
        }
    },
    // getContentAnchorEl: null,
    anchorOrigin: {
        vertical: "bottom",
        horizontal: "center"
    },
    transformOrigin: {
        vertical: "top",
        horizontal: "center"
    },
    variant: "menu"
};

export default function SelectField(props) {
    const { defaultValue, disabled, error, helperText, id, label, onChange, onDelete,
        linkTo, value, valueObjects, options, required, requiredComplete, selectAll, selectAllLabel, emptyMessage } = props;
    // `selectAll` used to be the only way to get the multi-select branch, so a caller who wanted
    // multi-select was forced to also take the "Select all" row. `multiple` separates the two;
    // selectAll still implies multiple, so every existing caller is unaffected.
    const isMultiple = props.multiple === true || selectAll === true;
    const dataCy = props['data-cy'];
    const [open, setOpen] = useState(false);
    const theme = useTheme();

    // Asterisk color based on required type
    const asteriskColor = requiredComplete ? theme.palette.error.main : theme.palette.primary.main;
    const labelSx = {
        ml: -1.6,
        mt: 0.5,
        ...((required || requiredComplete) && {
            '& .MuiFormLabel-asterisk': {
                color: asteriskColor,
            },
        }),
    };

    // For rendering chips - use valueObjects if provided, otherwise look up from options
    const displayValues = valueObjects || (Array.isArray(value) ? value : []).map(id => options?.find(opt => opt.value === id)).filter(Boolean);

    const isEmpty = !options || options.length === 0;
    const isDisabled = disabled || isEmpty;

    let isAllSelected = false;
    if (selectAll) {
        isAllSelected = options != null && options.length > 0 && value != null && value.length === options.length;
    }

    const intChange = (event) => {
        const values = event.target.value;
        if(!Array.isArray(values)) {
            return;
        }
        let value = values[values.length - 1];
        if (value === 'all') {
            setOpen(!open);
        }
        onChange(event);
    }

    const handleChange = (event) => {
        onChange(event);
    }

    return !isMultiple ?
        <FormControl sx={{ minWidth: '100%' }} disabled={isDisabled} error={!!error} data-cy={dataCy}>
            <InputLabel id={`${id}-label`} sx={labelSx} required={required || requiredComplete}>{label}</InputLabel>
            <Select
                labelId={`${id}-label`}
                id={`select-${id}`}
                value={value}
                onChange={handleChange}
                defaultValue={defaultValue ?? ''}
                input={<Input />}
                required={required}
                variant={props.variant != null ? props.variant : 'standard'}
            >
                {options != null && options !== '' &&
                    options.map((option, indx) => <MenuItem value={option.value} key={indx}>{option.label}</MenuItem>)}
            </Select>
            <FormHelperText sx={{ color: (isEmpty && emptyMessage) ? 'text.secondary' : error ? 'error.main' : 'text.secondary' }}>
                {isEmpty && emptyMessage ? emptyMessage : (typeof helperText === 'string' ? helperText : null) || (typeof error === 'string' ? error : null)}
            </FormHelperText>
        </FormControl>
        :
        <FormControl sx={{ minWidth: '100%' }} disabled={isDisabled} error={!!error} data-cy={dataCy}>
            <InputLabel id={`${id}-label`} sx={labelSx} required={required || requiredComplete}>{label}</InputLabel>
            <Select
                labelId={`${id}-multi-select-label`}
                id={`multi-select-${id}`}
                multiple
                value={value || []}
                onChange={intChange}
                defaultValue={defaultValue || ''}
                input={<Input />}
                required={required}
                renderValue={(selected) => (
                    <div style={{ display: "flex", flexWrap: "wrap" }}>
                        {displayValues.map((obj) => {
                            if(obj != null){
                                return (
                                    <Chip
                                        size='small'
                                        key={obj.value}
                                        label={
                                            obj?.label != null && obj?.label !== '' &&
                                            obj?.quantity != null && obj?.quantity !== '' ?
                                                obj.label + '(' + obj.quantity + ')' :
                                            obj?.label != null && obj?.label !== '' ? obj.label : 'None'
                                        }
                                        deleteIcon={
                                            <CancelIcon
                                                onMouseDown={(event) => event.stopPropagation()}
                                            />
                                        }
                                        sx={{ m: 0.5 }}
                                        onDelete={!disabled ? (e) => onDelete(e, obj) : null}
                                        onClick={linkTo != null && obj?.value != null && obj?.value !== '' ? () => window.location.href = linkTo + obj?.value : null}
                                    />
                                );    
                            }else {
                                return null;
                            }                                                    
                        })}
                    </div>
                )}
                MenuProps={MenuProps}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                open={open}
            >
                {selectAll && <MenuItem
                    value="all"
                    classes={{
                        root: isAllSelected ? {
                            backgroundColor: "rgba(0, 0, 0, 0.08)",
                            "&:hover": {
                                backgroundColor: "rgba(0, 0, 0, 0.08)"
                            }
                        } : ""
                    }}
                >
                    <ListItemIcon>
                        <Checkbox
                            // sx={{ '.MuiCheckbox-indeterminate': {color: 'primary.main'} }}
                            checked={isAllSelected}
                            indeterminate={options != null && value != null &&
                                value.length > 0 && value.length < options.length
                            }
                            indeterminateIcon={<IndeterminateCheckBoxIcon sx={{ color: 'secondary.main' }} />}
                        />
                    </ListItemIcon>
                    <ListItemText
                        primaryTypographyProps={{ fontWeight: 600 }}
                        primary={selectAllLabel}
                    />
                </MenuItem>}
                {options && options.length > 0 && options.map((option) => {
                    return (
                        <MenuItem key={option.value} value={option.value}>
                            <ListItemIcon>
                                <Checkbox checked={value?.length > 0 ? value.includes(option.value) : false} color='secondary' />
                            </ListItemIcon>
                            {option.quantity != null && option.quantity !== '' ?
                                <ListItemText primary={option.label + '(' + option.quantity + ')'} /> :
                                <ListItemText primary={option.label} />
                            }
                        </MenuItem>
                    )
                })}
            </Select>
            <FormHelperText sx={{ color: (isEmpty && emptyMessage) ? 'text.secondary' : error ? 'error.main' : 'text.secondary' }}>
                {isEmpty && emptyMessage ? emptyMessage : (typeof helperText === 'string' ? helperText : null) || (typeof error === 'string' ? error : null)}
            </FormHelperText>
        </FormControl>;
}